import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Container } from "@/components/ui/container";

const TermsAndConditionsPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 bg-gray-50">
        <Container className="py-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm p-8">
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms & Conditions – The LabSwAPP</h1>
                <p className="text-gray-600">Effective Date: 1 Sep 2025</p>
              </div>

              <div className="prose prose-gray max-w-none space-y-6">
                <p className="text-gray-700 leading-relaxed">
                  Welcome to The LabSwAPP, a digital marketplace where university labs can swap reagents. By creating an account and using our services, you agree to the following Terms & Conditions. Please read them carefully.
                </p>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Acceptance of Terms</h2>
                  <p className="text-gray-700">
                    By accessing or using The LabSwAPP, you agree to be bound by these Terms & Conditions, along with our{" "}
                    <Link to="/privacy" className="text-reagent-blue hover:underline">Privacy Policy</Link>. If you do not agree, you may not use the platform.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Eligibility</h2>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>You must be at least 18 years old to use The LabSwAPP.</li>
                    <li>The platform is intended for use by university labs, research institutions, and their authorized members.</li>
                    <li>By registering, you confirm that you are authorized to act on behalf of your lab or institution.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Account Registration and Security</h2>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>You must provide accurate and complete information when creating your account.</li>
                    <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
                    <li>You are responsible for all activities under your account.</li>
                    <li>
                      If you believe your account has been compromised, notify us immediately at{" "}
                      <a href="mailto:hello@labswapp.com" className="text-reagent-blue hover:underline">
                        hello@labswapp.com
                      </a>
                      .
                    </li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Use of the Marketplace</h2>
                  <p className="text-gray-700 mb-3">
                    You agree to use The LabSwAPP only for lawful purposes and in accordance with these Terms. Specifically, you must not:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>Post or swap reagents that are prohibited, illegal, or restricted under applicable laws.</li>
                    <li>Misrepresent the identity, quality, or availability of reagents.</li>
                    <li>Use the platform for commercial resale outside its intended scope.</li>
                    <li>Engage in fraud, misuse, or harmful behavior.</li>
                  </ul>
                  <p className="text-gray-700 mt-3">We reserve the right to suspend or terminate accounts that violate these terms.</p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Transactions Between Users</h2>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>The LabSwAPP facilitates connections between labs but does not own, sell, or guarantee the reagents exchanged.</li>
                    <li>All transactions are the responsibility of the participating labs.</li>
                    <li>Users must ensure compliance with applicable safety, ethical, and regulatory requirements in their jurisdiction.</li>
                    <li>The LabSwAPP is not liable for the quality, legality, safety, or delivery of reagents.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Data Storage and Privacy</h2>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>
                      Your account information and transaction history will be stored securely as outlined in our{" "}
                      <Link to="/privacy" className="text-reagent-blue hover:underline">Privacy Policy</Link>.
                    </li>
                    <li>You may control your notification preferences within the platform.</li>
                    <li>You may delete your account at any time, subject to legal retention requirements.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Intellectual Property</h2>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>The LabSwAPP platform, content, design, and trademarks are the property of The LabSwAPP and protected by intellectual property laws.</li>
                    <li>You may not copy, modify, or distribute platform content without prior written permission.</li>
                    <li>You retain rights to any content or data you upload but grant us a limited license to use it for operating the service.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Limitation of Liability</h2>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>The LabSwAPP provides the platform "as is" without warranties of any kind.</li>
                    <li>We are not responsible for reagent quality, compatibility, or any direct or indirect damages arising from their use.</li>
                    <li>To the maximum extent permitted by law, The LabSwAPP disclaims all liability related to user transactions.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Indemnification</h2>
                  <p className="text-gray-700 mb-3">
                    You agree to indemnify and hold harmless The LabSwAPP, its affiliates, and team members from any claims, damages, or liabilities arising out of:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>Your use of the marketplace.</li>
                    <li>Your violation of these Terms.</li>
                    <li>Your exchange or use of reagents.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Termination</h2>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>You may terminate your account at any time through the account settings.</li>
                    <li>We may suspend or terminate your access if you breach these Terms or use the platform unlawfully.</li>
                    <li>Upon termination, certain provisions (e.g., liability, indemnification) will continue to apply.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Changes to Terms</h2>
                  <p className="text-gray-700">
                    We may update these Terms from time to time. Updates will be posted on the platform, and continued use constitutes acceptance of the revised Terms.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">12. Governing Law</h2>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li><strong>For users in Europe:</strong> These Terms are governed by the laws of Spain.</li>
                    <li><strong>For users in the United States:</strong> These Terms are governed by the laws of the state of Delaware.</li>
                    <li>Disputes shall be resolved in the competent courts of the chosen jurisdiction, unless applicable law requires otherwise.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">13. Contact Us</h2>
                  <p className="text-gray-700 mb-3">If you have questions about these Terms, please contact us:</p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-700 font-medium">The LabSwAPP</p>
                    <p className="text-gray-700">
                      Email:{" "}
                      <a href="mailto:hello@labswapp.com" className="text-reagent-blue hover:underline">
                        hello@labswapp.com
                      </a>
                    </p>
                  </div>
                </section>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200">
                <Link
                  to="/"
                  className="inline-flex items-center text-reagent-blue hover:text-reagent-blue-dark font-medium"
                >
                  ← Back to Home
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </main>
      <Footer />
    </div>
  );
};

export default TermsAndConditionsPage;